import { RefObject, useEffect, useRef } from 'react';

type Handle = Readonly<{ id: number; stopPropagation: () => boolean }>;

/**
 * Keeps track of all registered useOnClickOutside consumers and allows
 * handlers to prevent clicks from propagating to previously registered
 * consumers.
 */
const handleStore = { handles: [] as Handle[] };

/**
 * Returns true if a later registered handle is preventing clicks from
 * propagating.
 */
const isHandleStopped = (handle: Handle) => {
    const handleIndex = handleStore.handles.findIndex(({ id }) => id === handle.id);
    return (
        handleIndex > -1 &&
        handleStore.handles.some(
            (storeHandle, index) => index > handleIndex && storeHandle.stopPropagation(),
        )
    );
};

/**
 * An effect which runs some side-effect if the user clicked outside of a
 * predefined list of elements.
 *
 * Example: Close modal windows and popups when someone "clicks off" of them.
 *
 * @param refs The element or array of elements that are "safe"
 * to click on. Clicking on these elements or their descendants in the DOM will
 * not trigger the event handler.
 * @param handler The event handler
 * that will be triggered if one of the elements not in the "safe" list is
 * clicked on.
 * @param enabled Enables or disables hook (defaults to `true`).
 */
export function useOnClickOutside(
    refs: RefObject<Element> | RefObject<Element>[],
    handler: (event: MouseEvent | TouchEvent) => void,
    enabled = true,
    stopPropagation = false,
) {
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const refList = useRef(refs);
    refList.current = refs;

    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    const stopPropagationRef = useRef(stopPropagation);
    stopPropagationRef.current = stopPropagation;

    // Using refs so that event listeners only need to be added/removed once.
    useEffect(() => {
        const hookHandle: Handle = {
            id: handleStore.handles.length,
            stopPropagation: () => stopPropagationRef.current,
        };

        const listener = (event: MouseEvent | TouchEvent) => {
            const { target } = event;
            const refsList = Array.isArray(refList.current)
                ? refList.current
                : [refList.current];

            if (
                enabledRef.current &&
                target instanceof Node &&
                !isHandleStopped(hookHandle) &&
                // Execute callback if clicked node is not contained in the supplied ref(s).
                !refsList.some((ref) => ref.current?.contains(target) ?? false)
            ) {
                handlerRef.current(event);
            }
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        handleStore.handles.push(hookHandle);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
            handleStore.handles = handleStore.handles.filter(
                (handle) => handle !== hookHandle,
            );
        };
    }, []);
}
